import React, { useEffect, useState, useCallback } from 'react';
import { ArrowUp } from 'lucide-react';

/**
 * Wave 3.99j — Global "Back to top" button.
 *
 * Renders a compact floating pill (icon + "Top" label) fixed to the
 * bottom-right of the viewport. Visibility rules:
 *
 *   • Hidden while the page is at (or near) the top.
 *   • Hidden when the page isn't actually scrollable — no point
 *     showing an escape hatch that has nowhere to escape to.
 *   • Shown otherwise. Click smoothly scrolls the window back to top.
 *
 * The button lives above regular content (z-40) but below toasts /
 * modal dialogs. Motion respects prefers-reduced-motion.
 *
 * Usage: drop `<ScrollToTopButton />` once inside any long page (e.g.
 * OrderDetail). No props are required.
 */
export function ScrollToTopButton({ threshold = 240 }) {
  const [visible, setVisible] = useState(false);

  const evaluate = useCallback(() => {
    // Prefer window scroll; some layouts wrap content in a scrollable
    // <main>, but our AppShell uses window scroll so this is fine.
    const doc = document.documentElement;
    const scrollY = window.scrollY || doc.scrollTop || 0;
    const viewportH = window.innerHeight;
    const totalH = doc.scrollHeight;
    // Page is only "worth scrolling" if content overflows by more
    // than a small tolerance — matches the user's "if there's nothing
    // further to scroll, hide" ask.
    const scrollable = totalH - viewportH > 80;
    setVisible(scrollable && scrollY > threshold);
  }, [threshold]);

  useEffect(() => {
    evaluate();
    window.addEventListener('scroll', evaluate, { passive: true });
    window.addEventListener('resize', evaluate);
    return () => {
      window.removeEventListener('scroll', evaluate);
      window.removeEventListener('resize', evaluate);
    };
  }, [evaluate]);

  const onClick = () => {
    const prefersReduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: prefersReduced ? 'auto' : 'smooth' });
  };

  // Render the button in the DOM always (avoids mount/unmount jitter);
  // toggle a class for animated fade.
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Scroll to top"
      data-testid="scroll-to-top-button"
      style={{
        position: 'fixed',
        right: '24px',
        bottom: '28px',
        zIndex: 40,
      }}
      className={[
        'inline-flex items-center gap-1 rounded-full pl-2.5 pr-3 py-1.5',
        'bg-[var(--ink-navy)] text-white text-xs font-medium shadow-lg',
        'hover:opacity-90 active:scale-95',
        'transition-[opacity,transform] duration-200',
        visible ? 'opacity-100 translate-y-0 pointer-events-auto'
                : 'opacity-0 translate-y-3 pointer-events-none',
      ].join(' ')}
    >
      <ArrowUp size={14} strokeWidth={2.2} />
      <span>Top</span>
    </button>
  );
}

export default ScrollToTopButton;
